import React, { useCallback, useEffect, useRef, useState } from 'react';
import { formatBalance } from '../../../lib/helpers/balance';
import { cantidadLegible } from '../../../lib/helpers/products';
import { apiErrorDetail } from '../../../context/api';

/**
 * «Miniestadísticas» del socio en un periodo (petición Kong House, 12-10-2026):
 * «poder ver periodos largos, incluso desde que se inscribió, y que el sistema
 * sume el total de dispensaciones y monedero sin sumar movimientos uno a uno».
 *
 * Los totales vienen SIEMPRE del servidor (GET /v1/member-summary), nunca de
 * sumar la lista del historial: esa lista llega limitada a 200 movimientos y el
 * total saldría corto sin avisar en cuanto el socio tuviera más. Las fechas son
 * jornadas del club (con su hora de corte), como en los informes.
 */

export const PRESETS = [
  { id: 'mes', label: 'Este mes' },
  { id: '3meses', label: 'Últimos 3 meses' },
  { id: 'anio', label: 'Este año' },
  { id: 'alta', label: 'Desde el alta' },
  { id: 'rango', label: 'Fechas…' },
];

const iso = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

/** Hace `meses` meses, con el día recortado al último del mes: el 31-may → 28/29-feb,
 *  no el 3-mar (Date desborda el día y se comería el 1 y el 2 de marzo). */
function haceMeses(hoy, meses) {
  const y = hoy.getFullYear();
  const m = hoy.getMonth() - meses;
  const ultimo = new Date(y, m + 1, 0).getDate();
  return new Date(y, m, Math.min(hoy.getDate(), ultimo));
}

/** La JORNADA en curso del club: antes de la hora de corte sigue siendo la de ayer. */
export function hoyDeJornada(corte = 0, ahora = new Date()) {
  return new Date(ahora.getTime() - (Number(corte) || 0) * 3600 * 1000);
}

/** { from, to } en 'YYYY-MM-DD' para un preset; null = sin límite. `hoy` = la jornada en curso. */
export function rangoDePreset(preset, hoy = new Date()) {
  const to = iso(hoy);
  if (preset === 'mes') return { from: iso(new Date(hoy.getFullYear(), hoy.getMonth(), 1)), to };
  if (preset === '3meses') return { from: iso(haceMeses(hoy, 3)), to };
  if (preset === 'anio') return { from: iso(new Date(hoy.getFullYear(), 0, 1)), to };
  return { from: null, to: null };   // desde el alta
}

const gramos = (n) => `${cantidadLegible(Number(n || 0).toFixed(3))} g`;
const unidades = (n) => `${cantidadLegible(Number(n || 0).toFixed(3))} u`;

function Dato({ etiqueta, valor, tono = '' }) {
  return (
    <div className="flex items-baseline justify-between gap-3 text-sm">
      <span className="text-[var(--wa-muted-foreground)]">{etiqueta}</span>
      <span className={`font-semibold tabular-nums ${tono}`}>{valor}</span>
    </div>
  );
}

function Tarjeta({ titulo, children }) {
  return (
    <div className="rounded-xl border border-[var(--wa-border)] px-4 py-3 space-y-1.5">
      <p className="text-xs font-semibold uppercase tracking-wide text-[var(--wa-muted-foreground)]">{titulo}</p>
      {children}
    </div>
  );
}

export default function ResumenSocio({ token, venueID, clientId, refreshKey = 0 }) {
  const [preset, setPreset] = useState('mes');
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [oculto, setOculto] = useState(false);
  // Hora de corte del club: la trae la propia respuesta. Hasta entonces, 0.
  const [corte, setCorte] = useState(0);
  // Solo la ÚLTIMA petición pinta: cambiando de preset rápido, una respuesta lenta
  // («Desde el alta») llegaba después de la rápida y dejaba totales de toda la vida
  // bajo «Este mes».
  const pidiendo = useRef(0);

  const rango = preset === 'rango'
    ? { from: desde || null, to: hasta || null }
    : rangoDePreset(preset, hoyDeJornada(corte));
  const rangoInvalido = rango.from && rango.to && rango.from > rango.to;
  // «Fechas…» sin ninguna fecha no pide nada: sería «Desde el alta» con otra etiqueta.
  const sinFechas = preset === 'rango' && !rango.from && !rango.to;

  const fetchResumen = useCallback(async () => {
    if (!token || !venueID || !clientId || rangoInvalido || sinFechas) return;
    const yo = ++pidiendo.current;
    setLoading(true);
    setError('');
    try {
      const qs = new URLSearchParams({ venue_id: venueID });
      if (rango.from) qs.set('from', rango.from);
      if (rango.to) qs.set('to', rango.to);
      const res = await fetch(`/v1/member-summary/${clientId}?${qs}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = await res.json().catch(() => ({}));
      if (yo !== pidiendo.current) return;
      // Ruta inexistente (backend aún sin desplegar): sin bloque, no un error rojo.
      if (res.status === 404 && json?.detail === 'Not Found') { setOculto(true); return; }
      if (!res.ok) throw new Error(apiErrorDetail(json, `Error ${res.status}`));
      setData(json);
      if (Number.isFinite(Number(json?.dia_de_corte))) setCorte(Number(json.dia_de_corte));
    } catch (e) {
      if (yo !== pidiendo.current) return;
      setData(null);
      setError(typeof e.message === 'string' ? e.message : 'No se pudo cargar el resumen.');
    } finally {
      if (yo === pidiendo.current) setLoading(false);
    }
  }, [token, venueID, clientId, rango.from, rango.to, rangoInvalido, sinFechas]);

  // refreshKey: una anulación en el historial de debajo → recalcular al momento.
  useEffect(() => { fetchResumen(); }, [fetchResumen, refreshKey]);

  const d = data?.dispensado;
  const a = data?.aportado;

  if (oculto) return null;

  return (
    <section aria-label="Resumen del periodo" className="mb-4 space-y-3">
      <div className="flex flex-wrap gap-1 rounded-xl bg-[var(--wa-card-muted)] p-1 w-fit">
        {PRESETS.map((p) => (
          <button
            key={p.id}
            type="button"
            aria-pressed={preset === p.id}
            onClick={() => setPreset(p.id)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              preset === p.id
                ? 'bg-[var(--background)] text-[var(--wa-foreground)] shadow-sm'
                : 'text-[var(--wa-muted-foreground)] hover:text-[var(--wa-foreground)]'
            }`}
          >
            {p.label}
          </button>
        ))}
      </div>

      {preset === 'rango' && (
        <div className="flex flex-wrap items-center gap-3 text-sm">
          <label className="flex items-center gap-2">
            <span className="text-[var(--wa-muted-foreground)]">Desde</span>
            <input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} className="wa-input h-9" aria-label="Desde" />
          </label>
          <label className="flex items-center gap-2">
            <span className="text-[var(--wa-muted-foreground)]">Hasta</span>
            <input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} className="wa-input h-9" aria-label="Hasta" />
          </label>
        </div>
      )}

      {rangoInvalido ? (
        <p className="text-sm text-[var(--destructive)]">La fecha «Desde» no puede ser posterior a «Hasta».</p>
      ) : error ? (
        <p className="text-sm text-[var(--destructive)]">{error}</p>
      ) : sinFechas ? (
        <p className="text-sm text-[var(--wa-muted-foreground)]">Elige al menos una fecha.</p>
      ) : loading ? (
        // Mientras llega el periodo nuevo NO se enseñan los números del anterior:
        // atenuados bajo la etiqueta nueva se leían como si fueran de ella.
        <p className="text-sm text-[var(--wa-muted-foreground)]">Calculando…</p>
      ) : d && a ? (
        <div className="grid gap-3 sm:grid-cols-2">
          <Tarjeta titulo="Dispensado">
            <Dato etiqueta="Importe" valor={formatBalance(d.importe)} />
            <Dato etiqueta="Gramos" valor={gramos(d.gramos)} />
            <Dato etiqueta="Unidades" valor={unidades(d.unidades)} />
            <Dato etiqueta="Ventas" valor={d.num_ventas} />
          </Tarjeta>
          <Tarjeta titulo="Monedero">
            <Dato etiqueta={`Recargas (${a.num_recargas})`} valor={formatBalance(a.recargas)} tono="text-[var(--success)]" />
            <Dato etiqueta={`Retiradas (${a.num_retiradas})`} valor={formatBalance(-a.retiradas)} tono="text-[var(--destructive)]" />
            <Dato etiqueta="Neto aportado" valor={formatBalance(a.neto)} />
            {data.cuotas > 0 && <Dato etiqueta="Cuotas de inscripción" valor={formatBalance(data.cuotas)} />}
          </Tarjeta>
        </div>
      ) : null}
    </section>
  );
}
